import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../config/supabase";

// ─── Qualification Field Templates ───
const DEFENSE_QUAL_FIELDS = [
  {
    id: "ranking", label: "Lead Ranking", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 1, dashboard_position: 1,
    dashboard_viz: "kpi_breakdown", is_ranking_field: true,
    csv_column_match: ["Ranking", "Lead Ranking", "Lead Status", "Lead Rating"],
    options: [
      { value: "hot", label: "Hot Lead for Follow Up", short_label: "Hot", color: "#22D3EE" },
      { value: "warm", label: "Warm for Nurture", short_label: "Warm", color: "#0891B2" },
      { value: "cold", label: "Cold Exploration", short_label: "Cold", color: "#0E7490" },
    ],
  },
  {
    id: "demo", label: "Received Demo", type: "yes_no", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 2, dashboard_position: 2,
    dashboard_viz: "kpi_rate", is_ranking_field: false,
    csv_column_match: ["Received Demo", "Demo", "Demo Given", "Demo Received"],
    options: null,
  },
  {
    id: "classification", label: "Classification", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 3, dashboard_position: 3,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Classification", "Lead Type", "Contact Type"],
    options: [
      { value: "new", label: "New Business" },
      { value: "existing", label: "Existing Customer" },
      { value: "partner", label: "Partner" },
      { value: "other", label: "Other" },
    ],
  },
  {
    id: "application", label: "Application of Interest", type: "multi_select", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 4, dashboard_position: 4,
    dashboard_viz: "multi_bar", is_ranking_field: false,
    csv_column_match: ["Application in Use", "Application", "Product Interest"],
    options: [
      { value: "supply_chain", label: "Supply Chain" },
      { value: "analytics", label: "Analytics" },
      { value: "sustainment", label: "Sustainment" },
      { value: "acquisition", label: "Acquisition" },
    ],
  },
];

const ENTERPRISE_TECH_QUAL_FIELDS = [
  {
    id: "ranking", label: "Lead Ranking", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 1, dashboard_position: 1,
    dashboard_viz: "kpi_breakdown", is_ranking_field: true,
    csv_column_match: ["Ranking", "Lead Ranking", "Lead Status", "Lead Score"],
    options: [
      { value: "hot", label: "Sales Qualified Lead", short_label: "SQL", color: "#22D3EE" },
      { value: "warm", label: "Marketing Qualified Lead", short_label: "MQL", color: "#0891B2" },
      { value: "cold", label: "Nurture", short_label: "Nurture", color: "#0E7490" },
    ],
  },
  {
    id: "demo", label: "Received Demo", type: "yes_no", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 2, dashboard_position: 2,
    dashboard_viz: "kpi_rate", is_ranking_field: false,
    csv_column_match: ["Received Demo", "Demo", "Demo Given"],
    options: null,
  },
  {
    id: "product_interest", label: "Product Interest", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 3, dashboard_position: 3,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Product Interest", "Product", "Solution Interest"],
    options: [
      { value: "platform", label: "Platform" },
      { value: "analytics", label: "Analytics" },
      { value: "integrations", label: "Integrations" },
      { value: "security", label: "Security" },
    ],
  },
  {
    id: "budget_timeline", label: "Budget Timeline", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 4, dashboard_position: 4,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Budget Timeline", "Timeline", "Purchase Timeline"],
    options: [
      { value: "this_quarter", label: "This Quarter" },
      { value: "next_quarter", label: "Next Quarter" },
      { value: "next_year", label: "Next Year" },
      { value: "exploring", label: "Exploring" },
    ],
  },
  {
    id: "decision_role", label: "Decision Role", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: false, mobile_position: 5, dashboard_position: 5,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Decision Role", "Role", "Buying Role"],
    options: [
      { value: "decision_maker", label: "Decision Maker" },
      { value: "influencer", label: "Influencer" },
      { value: "evaluator", label: "Evaluator" },
      { value: "end_user", label: "End User" },
    ],
  },
];

const HEALTHCARE_QUAL_FIELDS = [
  {
    id: "ranking", label: "Lead Ranking", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 1, dashboard_position: 1,
    dashboard_viz: "kpi_breakdown", is_ranking_field: true,
    csv_column_match: ["Ranking", "Lead Ranking", "Lead Status"],
    options: [
      { value: "hot", label: "Priority", short_label: "Priority", color: "#22D3EE" },
      { value: "warm", label: "Qualified", short_label: "Qualified", color: "#0891B2" },
      { value: "cold", label: "Early Stage", short_label: "Early", color: "#0E7490" },
    ],
  },
  {
    id: "demo", label: "Received Demo", type: "yes_no", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 2, dashboard_position: 2,
    dashboard_viz: "kpi_rate", is_ranking_field: false,
    csv_column_match: ["Received Demo", "Demo", "Product Demo"],
    options: null,
  },
  {
    id: "specialty", label: "Specialty", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 3, dashboard_position: 3,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Specialty", "Department", "Clinical Area"],
    options: [
      { value: "cardiology", label: "Cardiology" },
      { value: "oncology", label: "Oncology" },
      { value: "radiology", label: "Radiology" },
      { value: "emergency", label: "Emergency Medicine" },
      { value: "other", label: "Other" },
    ],
  },
  {
    id: "product_interest", label: "Product Interest", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 4, dashboard_position: 4,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Product Interest", "Product", "Solution"],
    options: [
      { value: "ehr", label: "EHR Integration" },
      { value: "imaging", label: "Medical Imaging" },
      { value: "telehealth", label: "Telehealth" },
      { value: "analytics", label: "Analytics" },
    ],
  },
  {
    id: "sample_requested", label: "Sample Requested", type: "yes_no", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 5, dashboard_position: 5,
    dashboard_viz: "kpi_rate", is_ranking_field: false,
    csv_column_match: ["Sample Requested", "Sample", "Trial Requested"],
    options: null,
  },
];

const FINANCIAL_QUAL_FIELDS = [
  {
    id: "ranking", label: "Lead Ranking", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 1, dashboard_position: 1,
    dashboard_viz: "kpi_breakdown", is_ranking_field: true,
    csv_column_match: ["Ranking", "Lead Ranking", "Lead Status"],
    options: [
      { value: "hot", label: "High Value", short_label: "High", color: "#22D3EE" },
      { value: "warm", label: "Qualified", short_label: "Qualified", color: "#0891B2" },
      { value: "cold", label: "Early Interest", short_label: "Early", color: "#0E7490" },
    ],
  },
  {
    id: "demo", label: "Received Demo", type: "yes_no", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 2, dashboard_position: 2,
    dashboard_viz: "kpi_rate", is_ranking_field: false,
    csv_column_match: ["Received Demo", "Demo", "Demonstration"],
    options: null,
  },
  {
    id: "product_interest", label: "Product Interest", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 3, dashboard_position: 3,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Product Interest", "Product", "Solution Interest"],
    options: [
      { value: "trading", label: "Trading Platform" },
      { value: "risk", label: "Risk Management" },
      { value: "compliance", label: "Compliance" },
      { value: "wealth", label: "Wealth Management" },
    ],
  },
  {
    id: "aum", label: "Assets Under Management", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 4, dashboard_position: 4,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["AUM", "Assets Under Management", "Asset Size"],
    options: [
      { value: "under_1b", label: "Under $1B" },
      { value: "1b_10b", label: "$1B - $10B" },
      { value: "10b_50b", label: "$10B - $50B" },
      { value: "over_50b", label: "Over $50B" },
    ],
  },
  {
    id: "compliance_status", label: "Compliance Status", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: false, mobile_position: 5, dashboard_position: 5,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Compliance Status", "Regulatory Status"],
    options: [
      { value: "compliant", label: "Fully Compliant" },
      { value: "in_progress", label: "In Progress" },
      { value: "needs_assessment", label: "Needs Assessment" },
    ],
  },
];

const GENERAL_QUAL_FIELDS = [
  {
    id: "ranking", label: "Lead Ranking", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 1, dashboard_position: 1,
    dashboard_viz: "kpi_breakdown", is_ranking_field: true,
    csv_column_match: ["Ranking", "Lead Ranking", "Lead Status", "Lead Rating"],
    options: [
      { value: "hot", label: "Hot", short_label: "Hot", color: "#22D3EE" },
      { value: "warm", label: "Warm", short_label: "Warm", color: "#0891B2" },
      { value: "cold", label: "Cold", short_label: "Cold", color: "#0E7490" },
    ],
  },
  {
    id: "demo", label: "Received Demo", type: "yes_no", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 2, dashboard_position: 2,
    dashboard_viz: "kpi_rate", is_ranking_field: false,
    csv_column_match: ["Received Demo", "Demo", "Demo Given", "Demo Received"],
    options: null,
  },
  {
    id: "classification", label: "Classification", type: "dropdown", required: false,
    show_on_mobile: true, show_on_dashboard: true, mobile_position: 3, dashboard_position: 3,
    dashboard_viz: "proportion_bar", is_ranking_field: false,
    csv_column_match: ["Classification", "Lead Type", "Contact Type"],
    options: [
      { value: "new", label: "New Business" },
      { value: "existing", label: "Existing Customer" },
      { value: "partner", label: "Partner" },
      { value: "other", label: "Other" },
    ],
  },
];

// Map template names to qualification fields
const TEMPLATE_QUAL_FIELDS = {
  defense: DEFENSE_QUAL_FIELDS,
  enterprise_tech: ENTERPRISE_TECH_QUAL_FIELDS,
  healthcare: HEALTHCARE_QUAL_FIELDS,
  financial: FINANCIAL_QUAL_FIELDS,
  general: GENERAL_QUAL_FIELDS,
};


// ─── Default Meeting Zones (Defense template) ───────────────────
const DEFAULT_MEETING_ZONES = [
  {
    id: "conference_room",
    name: "Conference Room",
    icon: "room",
    active: true,
    opens_at: "08:00",
    closes_at: "18:00",
    default_duration_minutes: 60,
    buffer_minutes: 5,
    max_concurrent: 1,
    requires_approval: "manager",
    order: 1,
  },
  {
    id: "demo_station_1",
    name: "Demo Station 1",
    icon: "demo",
    active: true,
    opens_at: "09:00",
    closes_at: "17:00",
    default_duration_minutes: 30,
    buffer_minutes: 5,
    max_concurrent: 1,
    requires_approval: "anyone",
    order: 2,
  },
  {
    id: "demo_station_2",
    name: "Demo Station 2",
    icon: "demo",
    active: true,
    opens_at: "09:00",
    closes_at: "17:00",
    default_duration_minutes: 30,
    buffer_minutes: 5,
    max_concurrent: 1,
    requires_approval: "anyone",
    order: 3,
  },
  {
    id: "demo_station_3",
    name: "Demo Station 3",
    icon: "demo",
    active: false,
    opens_at: "09:00",
    closes_at: "17:00",
    default_duration_minutes: 30,
    buffer_minutes: 5,
    max_concurrent: 1,
    requires_approval: "anyone",
    order: 4,
  },
  {
    id: "booth_meeting_room",
    name: "Booth Meeting Room",
    icon: "booth",
    active: true,
    opens_at: "09:00",
    closes_at: "17:00",
    default_duration_minutes: 45,
    buffer_minutes: 10,
    max_concurrent: 1,
    requires_approval: "anyone",
    order: 5,
  },
];

const DEFAULT_STAFFING_ZONES = [
  { id: "booth_entrance",  name: "Booth Entrance",       description: "Main entry point to the booth", active: true, order: 1 },
  { id: "demo_area",       name: "Main Demo Area",        description: "Primary product demonstration space", active: true, order: 2 },
  { id: "meeting_room",    name: "Meeting Room",          description: "Private meeting space", active: true, order: 3 },
  { id: "booth_floor",     name: "Booth Floor",           description: "General booth area", active: true, order: 4 },
  { id: "vip_lounge",      name: "VIP / Executive Lounge",description: "Executive and VIP engagement area", active: true, order: 5 },
];

const DEFAULT_STAFFING_ROLES = [
  { id: "team_lead",        label: "Team Lead",         color: "#22D3EE", order: 1 },
  { id: "demo_specialist",  label: "Demo Specialist",   color: "#0891B2", order: 2 },
  { id: "brand_ambassador", label: "Brand Ambassador",  color: "#67E8F9", order: 3 },
  { id: "lead_capture",     label: "Lead Capture",      color: "#164E63", order: 4 },
  { id: "booth_support",    label: "Booth Support",      color: "#0E7490", order: 5 },
  { id: "executive_host",   label: "Executive Host",    color: "#22D3EEaa", order: 6 },
];

const DEFENSE_DEFAULTS = {
  branding: { primary_color: "#22D3EE", secondary_color: "#0A1720", accent_color: "#0891B2", font_family: "Inter", logo_url: null, company_name: "EXIQ" },
  teams: [
    { name: "Army", color: "#22D3EE", order: 1 },
    { name: "Navy/USMC", color: "#38BDF8", order: 2 },
    { name: "Air Force/Space", color: "#2DD4BF", order: 3 },
    { name: "OSD/Joint", color: "#60A5FA", order: 4 },
    { name: "Civilian", color: "#818CF8", order: 5 },
    { name: "Intel", color: "#67E8F9", order: 6 },
  ],
  booth_types: [
    { name: "20x20 Booth", multiplier: 1.0, color: "#22D3EE" },
    { name: "10x20 Booth", multiplier: 1.05, color: "#38BDF8" },
    { name: "10x10 Booth", multiplier: 1.15, color: "#2DD4BF" },
    { name: "Table Setup", multiplier: 1.35, color: "#0891B2" },
    { name: "Kiosk", multiplier: 1.30, color: "#818CF8" },
  ],
  rankings: [
    { key: "hot", name: "Hot Lead for Follow Up", label: "Hot", color: "#22D3EE", order: 1 },
    { key: "warm", name: "Warm for Nurture", label: "Warm", color: "#0891B2", order: 2 },
    { key: "cold", name: "Cold Exploration", label: "Cold", color: "#0E7490", order: 3 },
    { key: "unranked", name: "Unranked", label: "\u2014", color: "#535C62", order: 4 },
  ],
  qualification_fields: DEFENSE_QUAL_FIELDS,
  scoring: {
    // Labels auto-generated from rankings + qualification fields — see getAutoFactorLabels()
    weights: { hotLeads: 20, demoRate: 20, warmLeads: 15, netNewPipeline: 15, existingPipeline: 10, totalPipeline: 10, costPerLead: 10 },
    factors: [
      { key: "hotLeads",        label: "Top Lead Rate",    thresholds: [30, 20, 10, 5],               higher_better: true  },
      { key: "demoRate",        label: "Demo Rate",        thresholds: [15, 10, 5, 2],                higher_better: true  },
      { key: "warmLeads",       label: "2nd Lead Rate",    thresholds: [25, 15, 8, 3],                higher_better: true  },
      { key: "netNewPipeline",  label: "Net-New Pipeline", thresholds: [2000000, 1000000, 500000, 0], higher_better: true  },
      { key: "existingPipeline",label: "Existing Pipeline",thresholds: [5000000, 2000000, 500000, 0], higher_better: true  },
      { key: "totalPipeline",   label: "Total Pipeline",   thresholds: [7000000, 3000000, 1000000, 0],higher_better: true  },
      { key: "costPerLead",     label: "Cost per Lead",    thresholds: [500, 1000, 2000, 5000],       higher_better: false },
    ],
  },
  kpi_labels: { net_new_pipeline: "Net-New Pipeline", existing_pipeline: "Existing Pipeline", total_pipeline: "Total Pipeline", meetings: "Appointments", demos: "Demos", leads: "Total Contacts", hot_rate: "Top Lead Rate", cost_per_lead: "Cost/Lead" },
  meeting_zones: DEFAULT_MEETING_ZONES,
  staffing_zones: DEFAULT_STAFFING_ZONES,
  staffing_roles: DEFAULT_STAFFING_ROLES,
  plan_features: {
    // Core (all plans)
    dashboard: true,
    events: true,
    teams: true,
    scorecard: true,
    leads: true,
    calendar: true,
    reps: true,
    upload: true,
    // Planning modules (Pro+)
    budget: true,
    annual_plan: true,
    meetings: true,
    // Intelligence (Pro+)
    ai_suite: true,
    // Advanced (Enterprise)
    employee_scheduling: true,
    crm: true,
    mobile_app: true,
    white_label: false,
    api_access: false,
    // Add-ons (any tier, purchased separately)
    addons: [],
  },
  fiscal_year: { start_month: 10 },
  currency: { code: "USD", symbol: "$", locale: "en-US" },
  date_presets: [
    { value: "all", label: "All Dates", visible: true },
    { value: "thisFY", label: "This FY", visible: true },
    { value: "lastFY", label: "Last FY", visible: true },
    { value: "thisQ", label: "This Quarter", visible: true },
    { value: "last90", label: "Last 90 Days", visible: true },
    { value: "custom", label: "Custom Range", visible: true },
  ],
};

const OrgSettingsContext = createContext(null);
export function OrgSettingsProvider({ orgId, children }) {
  const [settings, setSettings] = useState(DEFENSE_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [templateName, setTemplateName] = useState("defense");
  useEffect(() => {
    if (!orgId) { setSettings(DEFENSE_DEFAULTS); return; }
    loadOrgSettings(orgId);
  }, [orgId]);
  async function loadOrgSettings(id) {
    setLoading(true);
    try {
      // ── Parallel: org_settings + organizations fire at the same time ──────
      // Before: sequential — waited for org_settings before fetching organizations
      const [{ data, error }, { data: orgRow }] = await Promise.all([
        supabase.from("org_settings").select("*").eq("org_id", id).single(),
        supabase.from("organizations").select("template").eq("id", id).maybeSingle(),
      ]);
      if (error || !data) { setSettings(DEFENSE_DEFAULTS); return; }

      if (orgRow?.template) setTemplateName(orgRow.template);
      // Support both old custom_fields and new qualification_fields
      // Use nullish check (not length check) so an empty array [] is respected —
      // admins can intentionally clear all questions if they don't use Lead Qualification
      const qualFields = data.qualification_fields ?? data.custom_fields ?? null;
      setSettings({
        branding: data.branding || DEFENSE_DEFAULTS.branding,
        teams: data.teams?.length ? data.teams : DEFENSE_DEFAULTS.teams,
        booth_types: data.booth_types?.length ? data.booth_types : DEFENSE_DEFAULTS.booth_types,
        rankings: data.rankings?.length ? data.rankings : DEFENSE_DEFAULTS.rankings,
        qualification_fields: qualFields !== null ? qualFields : DEFENSE_DEFAULTS.qualification_fields,
        scoring: data.scoring || DEFENSE_DEFAULTS.scoring,
        kpi_labels: { ...DEFENSE_DEFAULTS.kpi_labels, ...(data.kpi_labels || {}) },
        fiscal_year: data.fiscal_year || DEFENSE_DEFAULTS.fiscal_year,
        currency: data.currency || DEFENSE_DEFAULTS.currency,
        date_presets: data.date_presets?.length ? data.date_presets : DEFENSE_DEFAULTS.date_presets,
        field_mappings: data.field_mappings || [],
        meeting_zones: data.meeting_zones?.length ? data.meeting_zones : DEFENSE_DEFAULTS.meeting_zones,
        staffing_zones: data.staffing_zones?.length ? data.staffing_zones : DEFENSE_DEFAULTS.staffing_zones,
        staffing_roles: data.staffing_roles?.length ? data.staffing_roles : DEFENSE_DEFAULTS.staffing_roles,
        plan_features: data.plan_features || DEFENSE_DEFAULTS.plan_features,
        // CRM integration settings
        crm_field_mappings:           data.crm_field_mappings           || [],
        sf_attribution_window_days:   data.sf_attribution_window_days   || 30,
      });
    } catch (err) {
      console.error("Error loading org settings:", err);
      setSettings(DEFENSE_DEFAULTS);
    } finally {
      setLoading(false);
    }
  }
  async function saveOrgSettings(updates) {
    if (!orgId) { setSettings(prev => ({ ...prev, ...updates })); return true; }
    try {
      const { error } = await supabase.from("org_settings").upsert({ org_id: orgId, ...updates, updated_at: new Date().toISOString() }, { onConflict: "org_id" });
      if (error) throw error;
      setSettings(prev => ({ ...prev, ...updates }));
      // Re-fetch from DB so all components get the authoritative saved state
      await loadOrgSettings(orgId);
      return true;
    } catch (err) {
      console.error("Error saving org settings:", err);
      return false;
    }
  }
  async function applyTemplate(name) {
    try {
      const { data, error } = await supabase.from("industry_templates").select("config").eq("name", name).single();
      if (error || !data) return false;
      setSettings(prev => ({ ...prev, ...data.config }));
      setTemplateName(name);
      return true;
    } catch (err) {
      console.error("Error applying template:", err);
      return false;
    }
  }

  // ─── Qualification Field Helpers ───
  const qualificationFields = settings.qualification_fields || [];

  const getField = (id) => qualificationFields.find(f => f.id === id) || null;

  const getRankingField = () => qualificationFields.find(f => f.is_ranking_field) || null;

  const getDashboardFields = () =>
    qualificationFields
      .filter(f => f.show_on_dashboard)
      .sort((a, b) => (a.dashboard_position || 99) - (b.dashboard_position || 99));

  const getMobileFields = () =>
    qualificationFields
      .filter(f => f.show_on_mobile)
      .sort((a, b) => (a.mobile_position || 99) - (b.mobile_position || 99));

  const value = {
    settings, loading, templateName,
    branding: settings.branding,
    teams: settings.teams,
    boothTypes: settings.booth_types,
    rankings: settings.rankings,
    qualificationFields,
    // Backward compat: customFields still works
    customFields: qualificationFields,
    scoring: settings.scoring,
    kpiLabels: settings.kpi_labels,
    fiscalYear: settings.fiscal_year,
    currency: settings.currency,
    datePresets: settings.date_presets,
    rankColors: Object.fromEntries((settings.rankings || []).map(r => [r.name, r.color])),
    boothColors: Object.fromEntries((settings.booth_types || []).map(b => [b.name, b.color])),
    boothMultipliers: Object.fromEntries((settings.booth_types || []).map(b => [b.name, b.multiplier])),
    teamColors: Object.fromEntries((settings.teams || []).map(t => [t.name, t.color])),
    visibleDatePresets: (settings.date_presets || []).filter(p => p.visible !== false),
    fyStartMonth: (settings.fiscal_year?.start_month || 10) - 1,
    meetingZones:   (settings.meeting_zones   || DEFENSE_DEFAULTS.meeting_zones),
    staffingZones:  (settings.staffing_zones  || DEFENSE_DEFAULTS.staffing_zones),
    staffingRoles:  (settings.staffing_roles  || DEFENSE_DEFAULTS.staffing_roles),
    planFeatures: (settings.plan_features || DEFENSE_DEFAULTS.plan_features),
    hasFeature: (key) => {
      const features = settings.plan_features || DEFENSE_DEFAULTS.plan_features;
      return features[key] === true || (features.addons || []).includes(key);
    },
    activeMeetingZones: (settings.meeting_zones || DEFENSE_DEFAULTS.meeting_zones).filter(z => z.active),
    // Qualification field helpers
    getField,
    getRankingField,
    getDashboardFields,
    getMobileFields,
    // Template qualification fields (for onboarding)
    TEMPLATE_QUAL_FIELDS,
    saveOrgSettings, applyTemplate,
    reload: () => orgId && loadOrgSettings(orgId),
  };
  return (
    <OrgSettingsContext.Provider value={value}>
      {children}
    </OrgSettingsContext.Provider>
  );
}
export function useOrgSettings() {
  const ctx = useContext(OrgSettingsContext);
  if (!ctx) throw new Error("useOrgSettings() must be used inside <OrgSettingsProvider>");
  return ctx;
}
